import { Drawer, useMediaQuery, useTheme, Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, Stack, Chip, Paper, CircularProgress } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import LogoutIcon from '@mui/icons-material/Logout'
import PhoneIcon from '@mui/icons-material/Phone'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import GavelIcon from '@mui/icons-material/Gavel'
import DrawIcon from '@mui/icons-material/Draw'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import kycIconSvg from 'src/assets/kyc approval.svg'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { MentorSetupContext } from 'src/mentor/context/MentorSetupContext'
import { preloadImage } from 'src/utils/imagePreloader'
import { logoutHandlerMentor } from 'src/mentor/helpers/utilityFunctions'
import LegalForm from 'src/mentor/components/LegalTerms/LegalForm'
import SignaturePad from 'src/consumer/components/Helpers/SignaturePad/SignaturePad'
import { postDataWithoutParams } from 'src/consumer/services/featureServices'
import { postMPDataWithoutParams } from 'src/mentor/services'
import { ProfileDataComponent } from '../MentorProfilePage'
import { mentorProfileSaveFunction } from '../MentorProfilePage/utils'
import {
  KycIcon,
  KycProceedButton,
  KycExploreAppButton,
  KycSubText,
  KycText,
} from '../../layout/Styles'
import PortalSwitchGateCard from './PortalSwitchGateCard'

// Phase 14e / 14an — applicant-facing support contacts. Points at the canonical AUMHUM
// human-support line (mirrors the consumer site's useContactNumber default) for both
// call and WhatsApp so the applicant can reach a real person while their KYC is in
// review. The backend's submission-confirmation email/WhatsApp templates carry the same
// number via the practitioner-application.support-{phone,whatsapp} properties; if those
// need to vary per env the right move is fetching them from the public config endpoint
// instead of duplicating here.
const SUPPORT_PHONE_DISPLAY = '+91 63609 03465'
const SUPPORT_PHONE_TEL = '+916360903465'
const SUPPORT_WHATSAPP_DISPLAY = '+91 63609 03465'
const SUPPORT_WHATSAPP_E164 = '916360903465'
const REVIEW_SLA_HOURS = 48

// Phase 14an / 14aw — render the submission timestamp in the viewer's local timezone
// with a short timezone label appended (e.g. "01 May 2026, 10:12 AM IST"). 14aw fixes
// the GMT-offset fallback Chromium emits instead of "IST" on Asia/Kolkata: Intl returns
// "GMT+5:30" because CLDR doesn't register a short abbreviation for India there. We
// substitute the canonical abbreviation from the curated map below for the IANA zones
// where AUMHUM has applicant traffic; everything else falls through to whatever Intl
// produces (typically "EST" / "PST" / "CET" — already correct in those locales).
//
// Add new entries here as new regions come online; the IANA zone is the canonical key
// (matches Intl.DateTimeFormat().resolvedOptions().timeZone exactly).
const TIMEZONE_ABBREVIATIONS = {
  'Asia/Kolkata': 'IST',
  'Asia/Calcutta': 'IST',
  'Asia/Colombo': 'IST',
  'Asia/Dubai': 'GST',
  'Asia/Singapore': 'SGT',
  'Asia/Hong_Kong': 'HKT',
  'Asia/Tokyo': 'JST',
  'Australia/Sydney': 'AEDT',
  'Australia/Melbourne': 'AEDT',
  'Australia/Perth': 'AWST',
  'Europe/London': 'GMT',
  'Europe/Paris': 'CET',
  'Europe/Berlin': 'CET',
  'Europe/Dublin': 'GMT',
  'America/New_York': 'EST',
  'America/Chicago': 'CST',
  'America/Denver': 'MST',
  'America/Los_Angeles': 'PST',
  'America/Toronto': 'EST',
  'UTC': 'UTC',
  'Etc/UTC': 'UTC',
}

function formatSubmissionTimestamp(rawTimestamp) {
  if (!rawTimestamp) return null
  // Accept ISO string ('2026-04-30T05:30:00Z') and the legacy 'YYYY-MM-DD HH:mm:ss' UTC
  // shape. dayjs parses both transparently; we use it to validate then hand the Date to
  // Intl for the actual formatting.
  const parsed = dayjs(rawTimestamp)
  if (!parsed.isValid()) return null
  const date = parsed.toDate()
  try {
    const ianaZone =
      typeof Intl !== 'undefined' && Intl.DateTimeFormat
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : ''
    const preferredAbbreviation = ianaZone && TIMEZONE_ABBREVIATIONS[ianaZone]

    // Render the date / time portion WITHOUT the timezone first so we have full control
    // over the trailing token (Intl's "GMT+5:30" emission is what we're trying to avoid
    // for IST applicants).
    const dateTimePart = new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date)

    if (preferredAbbreviation) {
      return `${dateTimePart} ${preferredAbbreviation}`
    }

    // No curated abbreviation — let Intl render the zone token natively (works fine for
    // most locales: "EST" / "PST" / "CET" come out correctly there).
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(date)
  } catch {
    // Old browsers without Intl.DateTimeFormat options support — fall back to dayjs +
    // the resolved timezone name from the runtime, so we still surface the zone.
    const tzGuess =
      typeof Intl !== 'undefined' && Intl.DateTimeFormat
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : ''
    const stamp = dayjs(date).format('DD MMM YYYY, h:mm A')
    return tzGuess ? `${stamp} (${tzGuess})` : stamp
  }
}

export default function KycPendingActionsPage() {
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { mentorProfileData, updateProfileData, allLegalPolicies, setDontShowKycModal, refreshLegalPolicies } =
    useContext(MentorProfileContext)
  const { allTags } = useContext(MentorSetupContext)
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false)
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)
  // Phase 14ax — agreement view/accept dialog state. When non-empty, opens the LegalForm
  // dialog scoped to that agreement id. Closing it auto-refreshes legal policies so the
  // unsigned list collapses as the applicant signs each one.
  const [activeAgreementId, setActiveAgreementId] = useState('')
  // Phase 14ax — signature persistence in-flight flag. The SignaturePad calls onChange
  // synchronously after the file upload returns; we then PUT the new id onto
  // /api/secure/teacher/save and refresh the profile so the chip flips to "on file".
  const [savingSignature, setSavingSignature] = useState(false)
  // Practitioner-prospect equivalent of /care/status' wellness-opt-out: the applicant can
  // self-withdraw and re-open without a support agent. Confirm dialog before either flip.
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false)
  const [withdrawInFlight, setWithdrawInFlight] = useState(false)
  const [reopenInFlight, setReopenInFlight] = useState(false)

  const kycStatus = mentorProfileData?.kycStatus
  const isWithdrawn = kycStatus === 'withdrawn'

  const handleEditProfile = () => {
    setProfileDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setProfileDrawerOpen(false)
  }

  const handleExploreApp = () => {
    // Set flags to indicate user explicitly chose consumer portal
    sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
    sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
    sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
    localStorage.removeItem('lastChosenFlow')
    // Navigate to consumer portal (root path)
    window.location.replace('/')
  }

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true)
  }

  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false)
    logoutHandlerMentor()
    navigate('/')
  }

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false)
  }

  // Mirrors POST /api/secure/me/patient-prospect/wellness-opt-out: lets ROLE_CREATOR self-pause
  // their pending application. The backend keeps the role + Practitioner record so a re-open
  // is a 1-click flip; support applicant queues filter on kycStatus=pending so the record
  // drops out of the active queue without a support agent needing to revoke anything.
  const handleWithdrawClick = () => {
    setWithdrawDialogOpen(true)
  }

  const handleWithdrawCancel = () => {
    if (withdrawInFlight) return
    setWithdrawDialogOpen(false)
  }

  const handleWithdrawConfirm = useCallback(async () => {
    setWithdrawInFlight(true)
    try {
      const response = await postMPDataWithoutParams(
        '/api/secure/practitioner-onboarding/withdraw',
        {},
      )
      if (response?.status === 200) {
        toast.success('Application withdrawn. You can re-open it any time.')
        if (typeof updateProfileData === 'function') {
          await updateProfileData()
        }
        setWithdrawDialogOpen(false)
      } else {
        toast.error(
          response?.data?.message ||
            'Could not withdraw the application — please try again.',
        )
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Could not withdraw the application — please try again.',
      )
    } finally {
      setWithdrawInFlight(false)
    }
  }, [updateProfileData])

  const handleReopenClick = useCallback(async () => {
    setReopenInFlight(true)
    try {
      const response = await postMPDataWithoutParams(
        '/api/secure/practitioner-onboarding/reopen',
        {},
      )
      if (response?.status === 200) {
        toast.success('Application re-opened. Our team will pick it back up.')
        if (typeof updateProfileData === 'function') {
          await updateProfileData()
        }
      } else {
        toast.error(
          response?.data?.message ||
            'Could not re-open the application — please try again.',
        )
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Could not re-open the application — please try again.',
      )
    } finally {
      setReopenInFlight(false)
    }
  }, [updateProfileData])

  // Preload KYC SVG immediately on mount for LCP optimization
  useEffect(() => {
    if (kycIconSvg && typeof kycIconSvg === 'string') {
      preloadImage(kycIconSvg, {
        as: 'image',
        fetchpriority: 'high',
      })
    }
  }, [])

  // Phase 14e — surface the submission datetime + a "Just submitted" framing when the
  // applicant has just landed here from /apply's "Submit for review" hand-off (the
  // PostLoginEnrichmentForm sets `practitionerApplicationJustSubmitted` in sessionStorage
  // right before window.location.replace). Stays sticky while they're on this page so a
  // mid-session refresh keeps the friendly framing; clears once they navigate elsewhere
  // (the layout redirects them off this page when KYC is approved).
  const justSubmitted = useMemo(() => {
    if (typeof window === 'undefined') return false
    return window.sessionStorage.getItem('practitionerApplicationJustSubmitted') === '1'
  }, [])

  const submittedAt = useMemo(
    () =>
      formatSubmissionTimestamp(
        mentorProfileData?.firstAppliedTime ||
          mentorProfileData?.submittedAt ||
          mentorProfileData?.createdTime,
      ),
    [mentorProfileData],
  )

  let headlineText
  let subText
  if (isWithdrawn) {
    headlineText = 'Application withdrawn'
    subText =
      'Your therapist application is paused. Our team won’t reach out unless you re-open it. ' +
      'You can keep using the AUMHUM wellness app in the meantime.'
  } else if (justSubmitted) {
    headlineText = "We've received your application"
    subText = `Thanks for applying to AUMHUM. Our onboarding team will review your application within ${REVIEW_SLA_HOURS} hours and reach out by email and WhatsApp. While you wait you can keep your profile fresh.`
  } else {
    headlineText = 'KYC Approval Pending'
    subText =
      'Your KYC approval is pending. We will send you an email when it is approved. In the meantime, you can edit your profile.'
  }

  // Phase 14ax — unsigned agreements surfaced directly on the gate page. Mirrors the
  // Edit-Profile-drawer Agreements tab list (which uses LegalPoliciesListingComponent +
  // LegalListItem) but is unconditional / inline here so the applicant can finish
  // signing without having to discover the right tab first. Sorted by applicableSince
  // descending so the freshest agreement leads.
  const unsignedAgreements = useMemo(() => {
    if (!Array.isArray(allLegalPolicies)) return []
    return allLegalPolicies
      .filter((agreement) => !agreement?.signed)
      .sort((a, b) => {
        const aSince = a?.applicableSince ? dayjs(a.applicableSince).valueOf() : 0
        const bSince = b?.applicableSince ? dayjs(b.applicableSince).valueOf() : 0
        return bSince - aSince
      })
  }, [allLegalPolicies])

  const signatureMediaId = mentorProfileData?.personalize?.signatureMediaId || ''

  const openAgreementDialog = useCallback((agreementId) => {
    if (!agreementId) return
    setActiveAgreementId(agreementId)
  }, [])

  const closeAgreementDialog = useCallback(async () => {
    setActiveAgreementId('')
    // Refresh the legal-policies list so the freshly-signed entry drops out of the
    // unsigned bucket on this page without needing a manual reload. LegalForm itself
    // also calls refreshLegalPolicies on success, but doing it here too is idempotent
    // and covers the dismiss-without-signing case (no-op refresh).
    if (typeof refreshLegalPolicies === 'function') {
      try {
        await refreshLegalPolicies()
      } catch {
        /* non-fatal — the list will catch up on the next route mount */
      }
    }
  }, [refreshLegalPolicies])

  // Phase 14ax — signature auto-save. Reuses the standard /api/secure/teacher/save
  // endpoint (the same one the Edit-Profile drawer hits) but with a thin payload built
  // from the current profile + the new signatureMediaId. The endpoint accepts the full
  // profile object — sending the spread keeps every other field unchanged. On success
  // we re-fetch via updateProfileData so the chip flips to "Signature on file" + the
  // SignaturePad's stored-image preview kicks in.
  const handleSignatureChange = useCallback(
    async (event) => {
      const newId = event?.target?.value || ''
      if (newId === signatureMediaId) return // no-op when SignaturePad re-emits the same id
      if (!mentorProfileData) {
        toast.error('Could not save the signature — please refresh and try again.')
        return
      }
      setSavingSignature(true)
      try {
        const toSubmit = mentorProfileSaveFunction({
          ...mentorProfileData,
          personalize: {
            ...(mentorProfileData?.personalize || {}),
            signatureMediaId: newId,
          },
        })
        const response = await postDataWithoutParams('/api/secure/teacher/save', toSubmit)
        if (response?.status === 200) {
          if (typeof updateProfileData === 'function') {
            await updateProfileData()
          }
          toast.success(newId ? 'Signature saved.' : 'Signature removed.')
        } else {
          toast.error(
            response?.data?.message ||
              'Could not save the signature — please try again.',
          )
        }
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            'Could not save the signature — please try again.',
        )
      } finally {
        setSavingSignature(false)
      }
    },
    [mentorProfileData, signatureMediaId, updateProfileData],
  )

  return (
    <>
      {/* Use simple Box instead of DialogContent for better LCP performance */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: { xs: 2, sm: 4 },
          gap: '40px',
          position: 'relative',
        }}
      >
        {/* Logout Button - Top Right */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '0.75rem', sm: '1rem' },
            right: { xs: '0.75rem', sm: '1rem' },
          }}
        >
          <Tooltip title="Logout" arrow>
            <IconButton
              onClick={handleLogoutClick}
              sx={{
                color: '#ef4444',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                },
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              }}
              aria-label="Logout"
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <KycIcon
          src={kycIconSvg}
          alt={headlineText}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          width="100"
          height="100"
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            maxWidth: 560,
            textAlign: 'center',
          }}
        >
          {!isWithdrawn && justSubmitted && (
            <Chip
              icon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
              label="Application submitted"
              size="small"
              sx={{
                backgroundColor: '#dcfce7',
                color: '#166534',
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontSize: '11px',
                mb: 0.5,
              }}
            />
          )}
          {isWithdrawn && (
            <Chip
              icon={<DoNotDisturbAltIcon sx={{ fontSize: 18 }} />}
              label="Application withdrawn"
              size="small"
              sx={{
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontSize: '11px',
                mb: 0.5,
              }}
            />
          )}
          <KycText>{headlineText}</KycText>
          <KycSubText>{subText}</KycSubText>
          {submittedAt && (
            <Typography
              variant="body2"
              sx={{ mt: 1.5, color: '#475569', fontSize: '14px' }}
            >
              Submitted on <strong>{submittedAt}</strong>
            </Typography>
          )}
        </Box>

        <PortalSwitchGateCard />

        {/* Phase 14e — applicant support contacts. Pure-CSS card so it renders early
            without waiting on heavier MUI surfaces; phone/WhatsApp links open native
            handlers on mobile and the WhatsApp web client on desktop. Hidden in the
            withdrawn state — there's nothing for support to chase up. */}
        {!isWithdrawn && (
        <Box
          sx={{
            width: '100%',
            maxWidth: 480,
            padding: '16px 20px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '11px',
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
              color: '#1e3151',
              fontWeight: 700,
            }}
          >
            Need to reach mentor support?
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            sx={{ width: '100%' }}
          >
            <Button
              component="a"
              href={`tel:${SUPPORT_PHONE_TEL}`}
              startIcon={<PhoneIcon />}
              variant="outlined"
              fullWidth
              sx={{
                textTransform: 'none',
                borderColor: '#1e3151',
                color: '#1e3151',
                '&:hover': { borderColor: '#1e3151', backgroundColor: '#eef2ff' },
              }}
            >
              Call {SUPPORT_PHONE_DISPLAY}
            </Button>
            <Button
              component="a"
              href={`https://wa.me/${SUPPORT_WHATSAPP_E164}`}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<WhatsAppIcon />}
              variant="contained"
              fullWidth
              sx={{
                textTransform: 'none',
                backgroundColor: '#25D366',
                '&:hover': { backgroundColor: '#1eb957' },
              }}
            >
              WhatsApp {SUPPORT_WHATSAPP_DISPLAY}
            </Button>
          </Stack>
        </Box>
        )}

        {/* Phase 14ax — pending agreements card. Same shape as the list inside
            Edit Profile → Agreements, lifted up here so the applicant doesn't have to
            discover the tab. Only renders when the applicant has at least one
            unsigned agreement; once they're all signed the card collapses entirely.
            Each row carries View+Accept actions both routed through the same
            LegalForm dialog (LegalForm renders the agreement HTML, the consent
            tickbox, and the Sign button — so a single affordance covers both
            "review" and "accept"). */}
        {!isWithdrawn && unsignedAgreements.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 560,
              p: { xs: 2, sm: 2.5 },
              border: '1px solid #fde68a',
              borderRadius: '12px',
              background:
                'linear-gradient(135deg, rgba(245, 158, 11, 0.06) 0%, rgba(255, 255, 255, 0.96) 60%)',
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
              useFlexGap
              flexWrap="wrap"
            >
              <GavelIcon sx={{ color: '#b45309', fontSize: 22 }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: '#1e3151' }}
              >
                Agreements pending your sign-off
              </Typography>
              <Chip
                size="small"
                label={`${unsignedAgreements.length} pending`}
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  backgroundColor: '#f59e0b',
                  color: '#fff',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5 }}
            >
              You can&apos;t complete onboarding until each of these is reviewed and
              accepted. Click View &amp; accept to read the full text and sign.
            </Typography>
            <Stack spacing={1.25}>
              {unsignedAgreements.map((agreement) => {
                const id = agreement?.agreementId
                if (!id) return null
                const lastUpdated = agreement?.applicableSince
                  ? dayjs(agreement.applicableSince).format('DD MMM YYYY')
                  : null
                return (
                  <Paper
                    key={`pending-${id}`}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      borderColor: '#fde68a',
                      background: 'rgba(255, 255, 255, 0.85)',
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                      useFlexGap
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: '#1e3151' }}
                        >
                          {agreement?.title || id}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {lastUpdated
                            ? `Last updated ${lastUpdated} · Not accepted`
                            : 'Not accepted'}
                        </Typography>
                      </Box>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ flexShrink: 0, mt: { xs: 1, sm: 0 } }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon fontSize="small" />}
                          onClick={() => openAgreementDialog(id)}
                          sx={{
                            textTransform: 'none',
                            borderColor: '#1e3151',
                            color: '#1e3151',
                          }}
                        >
                          View
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => openAgreementDialog(id)}
                          sx={{
                            textTransform: 'none',
                            backgroundColor: '#f59e0b',
                            '&:hover': { backgroundColor: '#d97706' },
                          }}
                        >
                          Accept
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                )
              })}
            </Stack>
          </Paper>
        )}

        {/* Phase 14ax — signature pad on the gate page itself so the applicant can
            capture / replace the signature image without having to open Edit Profile.
            Saves immediately on change via the standard /api/secure/teacher/save
            endpoint (binds to Practitioner.personalize.signatureMediaId). The
            'on file' chip flips when the saved profile re-hydrates after the PUT.
            Hidden in the withdrawn state — the application is paused, no point
            asking for a signature. */}
        {!isWithdrawn && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 560,
            p: { xs: 2, sm: 2.5 },
            border: '1px solid',
            borderColor: signatureMediaId ? 'rgba(16, 185, 129, 0.4)' : '#e2e8f0',
            borderRadius: '12px',
            background: signatureMediaId
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(255, 255, 255, 0.96) 60%)'
              : 'linear-gradient(135deg, rgba(124, 58, 237, 0.05) 0%, rgba(255, 255, 255, 0.96) 60%)',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 1 }}
            useFlexGap
            flexWrap="wrap"
          >
            <DrawIcon sx={{ color: '#7c3aed', fontSize: 22 }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: '#1e3151' }}
            >
              Your signature
            </Typography>
            {signatureMediaId && (
              <Chip
                size="small"
                icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                label="On file"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  backgroundColor: '#10b981',
                  color: '#fff',
                  '& .MuiChip-label': { px: 1 },
                  '& .MuiChip-icon': { color: '#fff', ml: 0.5 },
                }}
              />
            )}
            {savingSignature && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CircularProgress size={14} sx={{ color: '#7c3aed' }} />
                <Typography variant="caption" color="text.secondary">
                  Saving…
                </Typography>
              </Stack>
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1.5 }}
          >
            Draw with your mouse / trackpad / touchscreen, or upload a clean image.
            We use this on prescriptions and reports you generate after approval.
          </Typography>
          <SignaturePad
            id="signatureMediaId"
            value={signatureMediaId}
            onChange={handleSignatureChange}
            mode="both"
            title=""
            helperText="Saved automatically — no need to hit save."
          />
        </Paper>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginTop: '8px',
            alignItems: 'center',
          }}
        >
          {isWithdrawn ? (
            <>
              <KycProceedButton
                onClick={handleReopenClick}
                disabled={reopenInFlight}
                startIcon={
                  reopenInFlight ? (
                    <CircularProgress size={16} sx={{ color: 'inherit' }} />
                  ) : (
                    <RestartAltIcon />
                  )
                }
              >
                {reopenInFlight ? 'Re-opening…' : 'Re-open application'}
              </KycProceedButton>
              <KycExploreAppButton onClick={handleExploreApp}>
                Continue to Mental Wellness App
              </KycExploreAppButton>
            </>
          ) : (
            <>
              <KycProceedButton onClick={handleEditProfile}>Edit Profile</KycProceedButton>
              <KycExploreAppButton onClick={handleExploreApp}>
                Explore Mental Wellness App
              </KycExploreAppButton>
              <Button
                onClick={handleWithdrawClick}
                startIcon={<DoNotDisturbAltIcon fontSize="small" />}
                sx={{
                  textTransform: 'none',
                  color: '#64748b',
                  fontSize: '13px',
                  fontWeight: 500,
                  '&:hover': {
                    color: '#991b1b',
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                Withdraw application
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Profile Drawer - Right to left on desktop, bottom to top on mobile */}
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={profileDrawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : { xs: '100%', sm: '600px', md: '700px', lg: '800px' },
            height: isMobile ? '90vh' : '100vh',
            borderRadius: isMobile ? '16px 16px 0 0' : '0',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
      >
        {/* Drawer Header with Close Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <KycText sx={{ fontSize: '18px', fontWeight: 600 }}>Edit Profile</KycText>
          <IconButton onClick={handleCloseDrawer} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Profile Form Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <ProfileDataComponent
            profileData={mentorProfileData}
            updateProfileData={updateProfileData}
            enteringFirstTime={false}
            allLegalPolicies={allLegalPolicies}
            setDontShowKycModal={setDontShowKycModal}
            refreshLegalPolicies={refreshLegalPolicies}
            formContextData={{ domains: allTags }}
            slugInitialUrl={'https://aumhum.com/mentor/'}
            saveObjectTransform={(formData) => {
              // After save, the ProfileDataComponent will call updateProfileData
              // and then onCancel, which will close the drawer
              return mentorProfileSaveFunction(formData)
            }}
            onCancel={handleCloseDrawer}
          />
        </Box>
      </Drawer>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Confirm Logout
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to logout? You will need to sign in again to access your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button
            onClick={handleLogoutCancel}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none' }}
            startIcon={<LogoutIcon />}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Withdraw confirmation dialog — practitioner-prospect equivalent of the
          /care/status wellness-opt-out confirmation. The action is reversible
          (Re-open is a 1-click flip from this same page) so we keep the copy soft
          but still confirm to avoid stray clicks. */}
      <Dialog
        open={withdrawDialogOpen}
        onClose={handleWithdrawCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Withdraw your application?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            We&apos;ll pause the review and stop reaching out. Your profile and any
            uploads stay safe — you can re-open the application from this page any
            time.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can keep using the AUMHUM wellness app while the application is
            paused.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button
            onClick={handleWithdrawCancel}
            variant="outlined"
            disabled={withdrawInFlight}
            sx={{ textTransform: 'none' }}
          >
            Keep application
          </Button>
          <Button
            onClick={handleWithdrawConfirm}
            variant="contained"
            color="error"
            disabled={withdrawInFlight}
            sx={{ textTransform: 'none' }}
            startIcon={
              withdrawInFlight ? (
                <CircularProgress size={16} sx={{ color: 'inherit' }} />
              ) : (
                <DoNotDisturbAltIcon />
              )
            }
          >
            {withdrawInFlight ? 'Withdrawing…' : 'Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phase 14ax — agreement view+accept dialog. Mirrors the AgreementsDialog inside
          MentorProfilePage (LegalForm + showLayoutHeader/showPageHeader=false +
          afterSubmitFunction to close on success). LegalForm's afterSubmitFunction
          gets called only after a successful sign — so closing via the X just dismisses
          without claiming a sign. We still call closeAgreementDialog() in both paths so
          refreshLegalPolicies() runs idempotently. */}
      <Dialog
        open={Boolean(activeAgreementId)}
        onClose={closeAgreementDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: { xs: '95vh', sm: '90vh' },
          },
        }}
      >
        <DialogActions sx={{ p: 1, justifyContent: 'flex-end' }}>
          <IconButton onClick={closeAgreementDialog} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogActions>
        <DialogContent sx={{ pt: 0 }}>
          {activeAgreementId && (
            <LegalForm
              key={activeAgreementId}
              showLayoutHeader={false}
              showPageHeader={false}
              agreementId={activeAgreementId}
              afterSubmitFunction={closeAgreementDialog}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
